/**
 * InstallPrompt Component
 * Shows a prompt to install the PWA when available
 * Provides a user-friendly installation experience
 */

import { useState } from 'react';
import { usePWAInstall } from '../../hooks/usePWA';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

interface InstallPromptProps {
  showAsModal?: boolean;
  showAsBanner?: boolean;
  onInstall?: () => void;
  onDismiss?: () => void;
  showInstallModal?: boolean;
}

export default function InstallPrompt({
  showAsModal = false,
  showAsBanner = true,
  showInstallModal = true,
  onInstall,
  onDismiss,
}: InstallPromptProps) {
  const { canInstall, isInstalling, install } = usePWAInstall();
  const [isDismissed, setIsDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);

  if (!canInstall || isDismissed) {
    return null;
  }

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      onInstall?.();
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setShowModal(false);
    onDismiss?.();
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  // Modal version
  if ((showAsModal || showModal) && showInstallModal) {
    return (
      <Modal
        isOpen={showAsModal || showModal}
        onClose={handleDismiss}
        title="Install AI Prompts App"
        size="md"
      >
        <div className="space-y-6">
          {/* App Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>

          {/* Content */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Install AI Prompts App
            </h3>
            <p className="text-gray-600 mb-6">
              Get the full app experience with offline access, push notifications, 
              and faster loading times.
            </p>

            {/* Features */}
            <div className="grid grid-cols-1 gap-3 mb-6 text-left">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">Works offline</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">Faster loading</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">Push notifications</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">No app store needed</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="flex-1"
            >
              Maybe Later
            </Button>
            <Button
              onClick={handleInstall}
              isLoading={isInstalling}
              className="flex-1"
            >
              {isInstalling ? 'Installing...' : 'Install App'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Banner version
  if (showAsBanner && showInstallModal) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
          <div className="flex items-start space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">
                Install AI Prompts
              </h4>
              <p className="text-xs text-gray-600 mt-1">
                Get the app for offline access and faster loading
              </p>
              
              <div className="flex space-x-2 mt-3">
                <Button
                  size="sm"
                  onClick={handleInstall}
                  isLoading={isInstalling}
                  className="text-xs px-3 py-1"
                >
                  Install
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={showAsModal ? handleShowModal : handleDismiss}
                  className="text-xs px-3 py-1"
                >
                  {showAsModal ? 'Learn More' : 'Dismiss'}
                </Button>
              </div>
            </div>
            
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}